import React, { useEffect, useRef, useState } from 'react'
import { Form } from 'semantic-ui-react'
import {
    AppUpload,
    FormDropdownField,
    FormTextFormField,
    FormToggleFormField,
    SubmitCancelButtons,
} from 'elements'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FormErrorText, MediaService, ShowPhotoGpsService } from 'utility'
import { CompanyNameFormats } from './CompanyNameFormats'
import styled from 'styled-components'

const Shell = styled.div`
    color: #1c1917;

    .ui.form .fields {
        margin-bottom: 0;
    }

    @media (max-width: 720px) {
        .ui.form .fields {
            flex-direction: column !important;
        }

        .ui.form .fields > .field {
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
        }
    }
`

const Section = styled.section`
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    padding: 18px 18px 6px;
    margin-bottom: 14px;
`

const SectionTitle = styled.h3`
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.02em;
`

const SectionLead = styled.p`
    margin: 0 0 14px;
    color: #78716c;
    font-size: 13px;
    line-height: 1.4;
`

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 4px 0 8px;
`

const CompanySchema = yup.object().shape({
    TimeZone: yup.string().required('TimeZone is required'),
    CompanyLogo: yup.string().trim(FormErrorText.SpacesNotAllowed).strict(),
    AddressLine1: yup.string().trim(FormErrorText.SpacesNotAllowed).strict().required('AddressLine1 is required'),
    AddressLine2: yup.string().trim(FormErrorText.SpacesNotAllowed).strict().required('AddressLine2 is required'),
    City: yup.string().trim(FormErrorText.SpacesNotAllowed).strict().required('City is required'),
    State: yup.string().trim(FormErrorText.SpacesNotAllowed).strict().required('State is required'),
    Country: yup.string().trim(FormErrorText.SpacesNotAllowed).strict().required('Country is required'),
    Zipcode: yup.string().trim().matches(/^[a-zA-Z0-9\s-]+$/, 'Zipcode must be alphanumeric').required('Zipcode is required'),
    IsDefault: yup.boolean(),
    ShowPhotoGps: yup.boolean(),
})

// savedTimeZone / savedTimeZoneSettingId = time zone (and row) the server uses for inspection names.
export const CompanyAddEditModal = ({
    onClose,
    data,
    loading,
    timeZonesList,
    open = true,
    savedTimeZone,
    savedTimeZoneSettingId,
}) => {
    const {
        control,
        handleSubmit, reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CompanySchema),
        defaultValues: {
            ShowPhotoGps: true,
        },
    })
    const [files, setFiles] = useState([])
    const nameFormatsRef = useRef(null)
    const formTimeZone = watch('TimeZone')
    // Preview with the zone being chosen only when this row is (or will become) the one the
    // server reads - the row in use, or the first row when none has a zone yet.
    const editsZoneRow =
        !savedTimeZone || (data?.SettingId && data.SettingId === savedTimeZoneSettingId)
    const companyTimeZone = editsZoneRow
        ? formTimeZone || savedTimeZone
        : savedTimeZone

    const onLogoSelect = (list) => {
        setFiles([list])
    }

    const onLogoRemove = () => {
        setFiles([])
    }

    useEffect(() => {
        if (data?.SettingId) {
            reset({
                ...data,
                ShowPhotoGps: true,
            })
            setFiles([{ url: data.CompanyLogo, uid: new Date() }])
        } else {
            reset({
                AddressLine1: "",
                Zipcode: "",
                ShowPhotoGps: true,
            })
            setFiles([])
        }
    }, [data, reset])

    // Dedicated CompanySetting/ShowPhotoGps endpoint (scoped to caller's company)
    useEffect(() => {
        if (!open) return undefined
        let cancelled = false
        const loadShowPhotoGps = async () => {
            try {
                const res = await ShowPhotoGpsService.getCompany()
                if (!cancelled) {
                    setValue('ShowPhotoGps', ShowPhotoGpsService.effective(res))
                }
            } catch (error) {
                console.log('ShowPhotoGps company load error:', error)
            }
        }
        loadShowPhotoGps()
        return () => {
            cancelled = true
        }
    }, [open, data?.SettingId, setValue])

    const submit = async formData => {
        try {
            let result = {}
            if (files.length && !files?.[0]?.url && files?.[0]?.name) {
                result = await MediaService.uploadMedia({ media: files[0] })
            }
            const nameFormats = nameFormatsRef.current?.getFormatPayloads?.()
            const { ShowPhotoGps, ...companyFields } = formData
            onClose({
                ...companyFields,
                CompanyLogo: result?.ImagePathUrl || files?.[0]?.url || "",
                nameFormats,
                ShowPhotoGps,
            })
            setFiles([])
        } catch (error) {
            console.log("error:", error)
        }
    }
    const onCancel = () => {
        onClose()
    }
    return (
        <Shell>
            <Form noValidate>
                <Section>
                    <SectionTitle>{data?.SettingId ? 'Edit address' : 'New address'}</SectionTitle>
                    <SectionLead>Time zone and the address stored for this company.</SectionLead>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            label="Time zone"
                            name="TimeZone"
                            placeholder="Select a time zone"
                            errors={errors?.TimeZone}
                            defaultValue=""
                            options={timeZonesList}
                        />
                        <FormTextFormField
                            control={control}
                            label="Address line 1"
                            name="AddressLine1"
                            placeholder="Street address"
                            errors={errors?.AddressLine1}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="Address line 2"
                            name="AddressLine2"
                            placeholder="Apartment, suite, or unit"
                            errors={errors?.AddressLine2}
                            defaultValue=""
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormTextFormField
                            control={control}
                            label="City"
                            name="City"
                            placeholder="City"
                            errors={errors?.City}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="State"
                            name="State"
                            placeholder="State"
                            errors={errors?.State}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="Country"
                            name="Country"
                            placeholder="Country"
                            errors={errors?.Country}
                            defaultValue=""
                        />
                        <FormTextFormField
                            control={control}
                            label="Zip code"
                            name="Zipcode"
                            placeholder="Zip code"
                            errors={errors?.Zipcode}
                            type="text"
                        />
                    </Form.Group>
                </Section>
                <Section>
                    <SectionTitle>Logo and report options</SectionTitle>
                    <SectionLead>Shown on exported reports and the company record.</SectionLead>
                    <Form.Group widths="equal">
                        <AppUpload
                            onChange={onLogoSelect}
                            onRemove={onLogoRemove}
                            files={files}
                            label="Company logo"
                        />
                        <FormToggleFormField
                            control={control}
                            name="IsDefault"
                            label="Default company"
                            defaultValue={false}
                        />
                        <FormToggleFormField
                            control={control}
                            name="ShowPhotoGps"
                            label="Show GPS caption in PDF"
                            defaultValue
                        />
                    </Form.Group>
                </Section>
                <CompanyNameFormats
                    ref={nameFormatsRef}
                    enabled={open}
                    draftOnly
                    companyTimeZone={companyTimeZone}
                />
                <Actions>
                    <SubmitCancelButtons loading={loading} onSubmit={handleSubmit(submit)} onCancel={onCancel} />
                </Actions>
            </Form>
        </Shell>
    )
}
